export interface SolucaoService {
  id: string;
  badge: string;
  icon: string;
  title: string;
  description: string;
  highlights: string[];
  ctaLabel: string;
  ctaMessage: string;
  href?: string;
  accent: "primary" | "accent" | "secondary";
  image?: string;
  imageAlt?: string;
  visualEmail?: string;
}

export const solucoesHero = {
  badge: "Soluções digitais",
  title:
    "Soluções digitais para colocar sua empresa na internet e fazer ela crescer",
  subtitle:
    "Da criação do seu site à hospedagem, loja virtual, VPS, SEO e anúncios: a NXHOST oferece a estrutura e as estratégias que sua empresa precisa para ter uma presença digital profissional.",
  highlights: [
    "Criação de sites",
    "Hospedagem & VPS",
    "Lojas virtuais",
    "SEO & Google Ads",
    "Marketing digital",
    "E-mail profissional",
  ],
};

export const solucoesOverview = {
  badge: "Tudo em um só lugar",
  title: "Uma estrutura digital completa para o seu negócio",
  description:
    "Você não precisa contratar várias empresas para cuidar da sua presença online. A NXHOST reúne tecnologia, desenvolvimento, hospedagem e estratégias digitais para ajudar sua empresa a criar, divulgar e crescer na internet.",
};

export const overviewCards = [
  {
    icon: "ri-layout-masonry-line",
    title: "Criação de Sites",
    description: "Sites modernos, responsivos e pensados para converter visitantes em clientes.",
  },
  {
    icon: "ri-palette-line",
    title: "Criação de Logotipo",
    description: "Marca profissional e identidade visual para o digital e o impresso.",
  },
  {
    icon: "ri-server-line",
    title: "Hospedagem",
    description: "Estrutura estável, performática e segura para manter seu site sempre no ar.",
  },
  {
    icon: "ri-hard-drive-3-line",
    title: "VPS & Servidores",
    description: "Mais recursos, controle e liberdade para projetos que exigem mais.",
  },
  {
    icon: "ri-shopping-bag-3-line",
    title: "Loja Virtual",
    description: "Catálogo, carrinho e checkout para vender seus produtos pela internet.",
  },
  {
    icon: "ri-search-eye-line",
    title: "SEO",
    description: "Otimização para aparecer melhor nas pesquisas do Google.",
  },
  {
    icon: "ri-megaphone-line",
    title: "Marketing Digital",
    description: "Estratégia, conteúdo e campanhas para atrair mais oportunidades.",
  },
  {
    icon: "ri-google-fill",
    title: "Google Ads",
    description: "Anúncios para alcançar quem já está procurando pelo seu serviço.",
  },
  {
    icon: "ri-pages-line",
    title: "Landing Pages",
    description: "Páginas focadas em campanhas, captação de contatos e geração de leads.",
  },
];

export const solucoesServices: SolucaoService[] = [
  {
    id: "sites",
    badge: "Criação de sites",
    icon: "ri-layout-masonry-line",
    title: "Criação de Sites Profissionais",
    description:
      "Sites modernos, rápidos, responsivos e desenvolvidos para apresentar sua empresa com credibilidade e transformar visitantes em clientes.",
    highlights: [
      "Design profissional",
      "Responsivo para celular, tablet e computador",
      "Estrutura otimizada para SEO",
      "Integração com WhatsApp",
      "Formulários de contato",
      "Domínio personalizado",
      "Alta performance",
      "Estrutura pensada para conversão",
    ],
    ctaLabel: "Quero criar meu site",
    ctaMessage: "Ola! Quero criar um site profissional com a NXHOST.",
    href: "/sites",
    accent: "primary",
  },
  {
    id: "logotipo",
    badge: "Identidade visual",
    icon: "ri-palette-line",
    title: "Criação de Logotipo e Identidade Visual",
    description:
      "Criamos a marca da sua empresa com um logotipo profissional e uma identidade visual consistente para o digital e o impresso.",
    highlights: [
      "Logotipo profissional",
      "Manual de identidade visual",
      "Variações para fundo claro e escuro",
      "Arquivos vetoriais e para impressão",
      "Aplicação em redes sociais",
      "Paleta de cores e tipografia",
      "Versão para site e fachada",
    ],
    ctaLabel: "Quero criar meu logotipo",
    ctaMessage: "Ola! Quero criar um logotipo e identidade visual com a NXHOST.",
    accent: "accent",
    image:
      "https://readdy.ai/api/search-image?query=modern%20logo%20design%20and%20brand%20identity%20presentation%20on%20a%20designer%20desk%20with%20color%20swatches%2C%20typography%20samples%20and%20a%20tablet%2C%20clean%20minimal%20creative%20studio%2C%20premium%20professional&width=1200&height=900&seq=nx-sol-logo&orientation=landscape",
    imageAlt: "Criação de logotipo e identidade visual",
  },
  {
    id: "hospedagem",
    badge: "Hospedagem",
    icon: "ri-server-line",
    title: "Hospedagem de Sites",
    description:
      "Hospede seu site em uma estrutura preparada para oferecer desempenho, estabilidade e segurança para sua presença online.",
    highlights: [
      "Hospedagem profissional",
      "Alto desempenho",
      "SSL/HTTPS",
      "Segurança",
      "Backups",
      "E-mails profissionais",
      "Painel de gerenciamento",
      "Suporte especializado",
    ],
    ctaLabel: "Conhecer hospedagem",
    ctaMessage: "Ola! Quero conhecer os planos de hospedagem da NXHOST.",
    href: "/hospedagem",
    accent: "accent",
  },
  {
    id: "vps",
    badge: "VPS",
    icon: "ri-hard-drive-3-line",
    title: "VPS — Servidores Virtuais",
    description:
      "Mais controle, recursos e liberdade para aplicações, sites, sistemas e projetos que precisam de uma estrutura mais robusta.",
    highlights: [
      "Recursos dedicados",
      "Mais controle do ambiente",
      "Escalabilidade",
      "Segurança",
      "Flexibilidade",
      "Ideal para sistemas e aplicações",
      "Projetos de maior demanda",
    ],
    ctaLabel: "Conhecer VPS",
    ctaMessage: "Ola! Quero conhecer os planos de VPS da NXHOST.",
    href: "/vps",
    accent: "secondary",
    image:
      "https://readdy.ai/api/search-image?query=modern%20cloud%20server%20infrastructure%20with%20glowing%20blue%20LED%20racks%20inside%20a%20dark%20data%20center%2C%20premium%20technology%20photography%2C%20clean%20and%20professional&width=1200&height=900&seq=nx-sol-vps&orientation=landscape",
    imageAlt: "Infraestrutura de servidores em nuvem e data center",
  },
  {
    id: "loja",
    badge: "Loja virtual",
    icon: "ri-shopping-bag-3-line",
    title: "Criação de Loja Virtual",
    description:
      "Transforme sua empresa em um negócio online com uma loja virtual profissional, moderna e preparada para vender pela internet.",
    highlights: [
      "Design profissional",
      "Catálogo de produtos",
      "Carrinho de compras",
      "Checkout",
      "Integração com pagamentos",
      "Integração com WhatsApp",
      "Estrutura responsiva",
      "SEO para produtos",
      "Preparada para campanhas de marketing",
    ],
    ctaLabel: "Quero criar minha loja",
    ctaMessage: "Ola! Quero criar uma loja virtual com a NXHOST.",
    href: "/lojas-virtuais",
    accent: "secondary",
    image:
      "https://readdy.ai/api/search-image?query=modern%20online%20store%20ecommerce%20website%20displayed%20on%20a%20desktop%20monitor%20and%20smartphone%20with%20product%20cards%20and%20shopping%20cart%2C%20bright%20clean%20studio%20setup%2C%20professional&width=1200&height=900&seq=nx-sol-loja&orientation=landscape",
    imageAlt: "Loja virtual moderna exibida em computador e celular",
  },
  {
    id: "seo",
    badge: "SEO",
    icon: "ri-search-eye-line",
    title: "SEO — Mais visibilidade no Google",
    description:
      "Otimize seu site para melhorar sua presença nos mecanismos de busca e aumentar as oportunidades de encontrar novos clientes através da pesquisa orgânica.",
    highlights: [
      "Otimização técnica",
      "Palavras-chave",
      "Estrutura de conteúdo",
      "Meta títulos e descrições",
      "URLs amigáveis",
      "Performance",
      "Google Search Console",
      "Estratégia de conteúdo",
    ],
    ctaLabel: "Quero melhorar meu SEO",
    ctaMessage: "Ola! Quero melhorar o SEO do meu site com a NXHOST.",
    accent: "accent",
  },
  {
    id: "marketing",
    badge: "Marketing digital",
    icon: "ri-megaphone-line",
    title: "Marketing Digital",
    description:
      "Estratégias digitais para aumentar a visibilidade da sua marca, atrair pessoas interessadas e gerar novas oportunidades para o seu negócio.",
    highlights: [
      "Estratégia digital",
      "Conteúdo",
      "Landing pages",
      "Campanhas",
      "Captação de leads",
      "Funis de conversão",
      "Análise de resultados",
      "Otimização de campanhas",
    ],
    ctaLabel: "Falar sobre marketing",
    ctaMessage: "Ola! Quero falar sobre marketing digital com a NXHOST.",
    accent: "primary",
  },
  {
    id: "googleads",
    badge: "Google Ads",
    icon: "ri-google-fill",
    title: "Anúncios no Google",
    description:
      "Coloque sua empresa na frente de pessoas que já estão procurando pelos produtos ou serviços que você oferece.",
    highlights: [
      "Google Ads",
      "Campanhas de pesquisa",
      "Rede de Display",
      "Remarketing",
      "Segmentação",
      "Palavras-chave",
      "Monitoramento",
      "Otimização de campanhas",
    ],
    ctaLabel: "Quero anunciar no Google",
    ctaMessage: "Ola! Quero anunciar no Google com a NXHOST.",
    accent: "accent",
    image:
      "https://readdy.ai/api/search-image?query=search%20engine%20results%20page%20and%20advertising%20analytics%20growth%20chart%20on%20a%20modern%20laptop%20in%20a%20bright%20office%2C%20digital%20marketing%20concept%2C%20clean%20professional&width=1200&height=900&seq=nx-sol-ads&orientation=landscape",
    imageAlt: "Anúncios e resultados de campanhas no Google",
  },
  {
    id: "landing",
    badge: "Landing pages",
    icon: "ri-pages-line",
    title: "Landing Pages que Geram Oportunidades",
    description:
      "Páginas desenvolvidas com foco em campanhas, captação de contatos, divulgação de serviços e geração de leads.",
    highlights: [
      "Design focado em conversão",
      "Estrutura objetiva",
      "Formulários",
      "WhatsApp",
      "Integração com campanhas",
      "SEO",
      "Responsividade",
      "Analytics",
    ],
    ctaLabel: "Criar minha Landing Page",
    ctaMessage: "Ola! Quero criar uma landing page com a NXHOST.",
    accent: "primary",
  },
  {
    id: "dominio",
    badge: "Domínio e e-mail",
    icon: "ri-mail-send-line",
    title: "Domínio e E-mail Profissional",
    description:
      "Fortaleça a imagem da sua empresa utilizando domínio próprio e e-mails profissionais personalizados.",
    highlights: [
      "Domínio personalizado",
      "E-mail profissional",
      "Identidade empresarial",
      "Configuração técnica",
      "Maior credibilidade",
    ],
    ctaLabel: "Quero um e-mail profissional",
    ctaMessage: "Ola! Quero um dominio e e-mail profissional com a NXHOST.",
    href: "/dominios",
    accent: "secondary",
    visualEmail: "contato@suaempresa.com.br",
  },
];

export const solucoesFlow = {
  badge: "Solução completa",
  title: "Do primeiro clique ao crescimento da sua empresa",
  description:
    "A NXHOST pode cuidar de diferentes etapas da sua presença digital: criar sua estrutura, hospedar seus projetos, colocar sua empresa no Google e ajudar você a transformar presença online em novas oportunidades.",
};

export const flowSteps = [
  { icon: "ri-lightbulb-line", label: "Ideia", description: "Entendemos o objetivo do seu projeto." },
  { icon: "ri-layout-masonry-line", label: "Site", description: "Criamos sua estrutura digital." },
  { icon: "ri-server-line", label: "Hospedagem", description: "Colocamos tudo no ar com segurança." },
  { icon: "ri-search-eye-line", label: "Google", description: "Trabalhamos a visibilidade na busca." },
  { icon: "ri-user-smile-line", label: "Clientes", description: "Atraímos pessoas interessadas." },
  { icon: "ri-line-chart-line", label: "Crescimento", description: "Evoluímos com o seu negócio." },
];

export const whyCards = [
  {
    icon: "ri-cpu-line",
    title: "Tecnologia",
    description: "Soluções modernas para empresas de diferentes tamanhos.",
  },
  {
    icon: "ri-speed-up-line",
    title: "Performance",
    description: "Sites e estruturas pensados para oferecer uma boa experiência aos usuários.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Segurança",
    description: "Boas práticas para proteger sua presença digital e seus projetos.",
  },
  {
    icon: "ri-route-line",
    title: "Estratégia",
    description: "Não criamos apenas páginas: pensamos no objetivo do negócio.",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "Atendimento",
    description: "Suporte e comunicação próxima durante o projeto.",
  },
  {
    icon: "ri-line-chart-line",
    title: "Crescimento",
    description: "Estruturas preparadas para acompanhar a evolução da sua empresa.",
  },
];

export const audienceTypes = [
  { icon: "ri-store-2-line", title: "Pequenas empresas", description: "Estrutura profissional para começar bem." },
  { icon: "ri-user-star-line", title: "Profissionais autônomos", description: "Presença digital para conquistar clientes." },
  { icon: "ri-briefcase-4-line", title: "Prestadores de serviços", description: "Apresente e venda seus serviços online." },
  { icon: "ri-shopping-bag-3-line", title: "Lojas e e-commerce", description: "Venda seus produtos pela internet." },
  { icon: "ri-building-2-line", title: "Empresas B2B", description: "Credibilidade e geração de oportunidades." },
  { icon: "ri-award-line", title: "Profissionais liberais", description: "Fortaleça sua marca e sua autoridade." },
  { icon: "ri-rocket-2-line", title: "Startups", description: "Estrutura ágil para crescer rápido." },
  { icon: "ri-map-pin-2-line", title: "Negócios locais", description: "Seja encontrado na sua região." },
  { icon: "ri-line-chart-line", title: "Empresas em expansão", description: "Soluções que acompanham seu crescimento." },
];

export const combos = [
  {
    icon: "ri-global-line",
    title: "Presença Digital",
    subtitle: "Para começar do jeito certo",
    items: ["Site", "Hospedagem", "Domínio", "E-mail profissional"],
    accent: "primary" as const,
  },
  {
    icon: "ri-shopping-bag-3-line",
    title: "Loja Online",
    subtitle: "Para vender pela internet",
    items: ["Loja Virtual", "Hospedagem", "SEO", "Marketing"],
    accent: "secondary" as const,
  },
  {
    icon: "ri-line-chart-line",
    title: "Crescimento",
    subtitle: "Para atrair mais clientes",
    items: ["Site", "SEO", "Google Ads", "Landing Page"],
    accent: "accent" as const,
  },
  {
    icon: "ri-shield-keyhole-line",
    title: "Estrutura Profissional",
    subtitle: "Para projetos de alta demanda",
    items: ["Site", "VPS", "Segurança", "E-mail profissional"],
    accent: "primary" as const,
  },
];

export const solucoesProcess = {
  badge: "Como funciona",
  title: "Como funciona",
  steps: [
    {
      icon: "ri-chat-3-line",
      title: "Entendemos",
      description: "Conhecemos sua empresa, seus objetivos e suas necessidades.",
    },
    {
      icon: "ri-route-line",
      title: "Planejamos",
      description: "Definimos a melhor estrutura e as soluções para o projeto.",
    },
    {
      icon: "ri-code-s-slash-line",
      title: "Desenvolvemos",
      description: "Criamos, configuramos e colocamos sua solução digital em funcionamento.",
    },
    {
      icon: "ri-line-chart-line",
      title: "Evoluímos",
      description: "Acompanhamos oportunidades de melhoria, divulgação e crescimento.",
    },
  ],
};

export const solucoesFaqs = [
  {
    question: "A NXHOST cria sites personalizados?",
    answer:
      "Sim. Os projetos podem ser estruturados de acordo com o objetivo, segmento e identidade da empresa.",
  },
  {
    question: "Vocês também oferecem hospedagem?",
    answer:
      "Sim. A NXHOST oferece soluções de hospedagem para sites e projetos digitais.",
  },
  {
    question: "Posso contratar apenas um serviço?",
    answer:
      "Sim. Os serviços podem ser contratados individualmente ou combinados de acordo com a necessidade do projeto.",
  },
  {
    question: "Vocês criam lojas virtuais?",
    answer:
      "Sim. Desenvolvemos estruturas de e-commerce para empresas que desejam vender seus produtos pela internet.",
  },
  {
    question: "Vocês trabalham com SEO?",
    answer:
      "Sim. Trabalhamos com otimização e estratégias para melhorar a presença orgânica nos mecanismos de busca.",
  },
  {
    question: "Vocês fazem anúncios no Google?",
    answer:
      "Sim. Podemos estruturar e gerenciar campanhas de Google Ads de acordo com os objetivos da empresa.",
  },
  {
    question: "Vocês atendem empresas de qualquer cidade?",
    answer:
      "Sim. Os serviços digitais podem ser realizados remotamente para empresas de diferentes regiões.",
  },
];

export const solucoesCta = {
  title: "Sua empresa pronta para crescer na internet?",
  description:
    "Conte para a NXHOST o que você precisa. Vamos analisar seu projeto e indicar as soluções mais adequadas para sua empresa.",
  strip: "Sites • Hospedagem • VPS • Lojas Virtuais • SEO • Marketing • Google Ads",
};